<template>
  <div class="company-map ml-4 mr-4">
    <div v-if="type === 'create'">
      <div class="row py-3 align-items-center">
        <div class="col company-h1">
          <button type="button"
                  class="btn btn-back"
                  @click="setCompanyStep('companies-info')"
          />
          Create Company
        </div>
        <div class="col text-right">
          <button type="button"
                  class="btn btn-primary ld-ext-right"
                  :class="loadingButton ? 'running' : ''"
                  :disabled="buttonDisabled"
          >
            Save
            <div class="ld ld-ball ld-flip" />
          </button>
        </div>
      </div>
      <companies-steps-menu type="create" />
    </div>
    <div v-if="type === 'edit'">
      <div class="row py-3 align-items-center company-header">
        <div class="col-4">
          <h2 class="company-h1 mr-3">
            <button type="button"
                    class="btn btn-back"
                    @click="$router.push('/companies')"
            />
            {{ company.name }}
          </h2>
          <template v-if="company.status">
            <span v-if="company.status.name === 'Active'"
                  :class="'btn-status btn-active'"
            >{{ company.status.name }}</span>
            <span v-if="company.status.name === 'Inactive'"
                  :class="'btn-status btn-closed'"
            >{{ company.status.name }}</span>
            <span v-if="company.status.name === 'Setup Incomplete'"
                  :class="'btn-status btn-ncover info-company'"
            >{{ company.status.name }}
              <div class="btn btn-link p-0 tippy-popup tippy-info" @mouseover="showTooltipPrefix = true"
                   @mouseout="showTooltipPrefix = false"
              >
                <i class="fa fa-info-circle" />
              </div>
              <div class="popover tippy-info-popover" v-if="showTooltipPrefix">
                Setup incomplete is present when the company doesn't have one or more of the following: move sizes, arrival windows, merchant account, rates and coverage.
              </div>
            </span>
          </template>
        </div>
        <div class="col-8 d-flex align-items-center justify-content-end">
          <companies-visibility-bar
            :company="company"
            @saved="$emit('saved')"
            v-if="$_can('companies.review.request')"
          />
          <button type="button"
                  class="btn btn-primary ld-ext-right ml-5"
                  :class="loadingButton ? 'running' : ''"
                  :disabled="buttonDisabled"
          >
            Save
            <div class="ld ld-ball ld-flip" />
          </button>
        </div>
      </div>
    </div>

    <div class="mb-3 w-100">
      <h5 class="companies-sub-page-title">Getting started</h5>
      <div class="mod-24-settings">
        <p>In Mod24 settings section you can adjust: </p>
        <ul>
          <li><b>Website details</b> - Information that will be publicly available for potential customers, such as your logo, description, photos, etc.</li>
          <li><b>Tier level</b> - Type of subscription and it's options, related to being visible as a company on Mod24 website</li>
          <li><b>Billing info</b> - Choosing how you want to receive what you've earned from online bookings</li>
          <li><b>Company licence</b> - Entering a valid company licence</li>
        </ul>
      </div>
    </div>

    <p class="mt-3 submit-text">To get started with our online services please submit the request below after you've completed the checklist! Ensure you've filled in the request details.</p>

    <div class="request-checklist">
      <p class="request-checklist-title">Visibility request checklist</p>
      <div class="request-checklist-items">
        <div class="request-checklist-item rounded-top">
          <span :class="{'request-checklist-item-checkbox-active': checkList.licence_number, 'request-checklist-item-checkbox': !checkList.licence_number}">
            <span class="mod-icon-check" style="width: 10px; height: 10px" />
          </span>
          <label><a
            :href="`/companies/edit/${$route.params.id}?tab=companies-licence`"
          >Company licence</a></label>
        </div>
        <div class="request-checklist-item">
          <span :class="{'request-checklist-item-checkbox-active': checkList.request_note, 'request-checklist-item-checkbox': !checkList.request_note}">
            <span class="mod-icon-check" style="width: 10px; height: 10px" />
          </span>
          <label><a
            :href="`/companies/edit/${$route.params.id}?tab=companies-licence`"
          >Visibility request note <i>(optional)</i></a></label>
        </div>
        <div class="request-checklist-item">
          <span :class="{'request-checklist-item-checkbox-active': checkList.logo, 'request-checklist-item-checkbox': !checkList.logo}">
            <span class="mod-icon-check" style="width: 10px; height: 10px" />
          </span>
          <label><a
            :href="`/companies/edit/${$route.params.id}?tab=website-details`"
          >Logo</a></label>
        </div>
        <div class="request-checklist-item">
          <span :class="{'request-checklist-item-checkbox-active': checkList.company_description, 'request-checklist-item-checkbox': !checkList.company_description}">
            <span class="mod-icon-check" style="width: 10px; height: 10px" />
          </span>
          <label><a
            :href="`/companies/edit/${$route.params.id}?tab=website-details`"
          >Company description</a></label>
        </div>
        <div class="request-checklist-item">
          <span :class="{'request-checklist-item-checkbox-active': checkList.gallery_of_images, 'request-checklist-item-checkbox': !checkList.gallery_of_images}">
            <span class="mod-icon-check" style="width: 10px; height: 10px" />
          </span>
          <label><a
            :href="`/companies/edit/${$route.params.id}?tab=website-details`"
          >Gallery of images <i>(optional)</i></a></label>
        </div>
        <div class="request-checklist-item">
          <span :class="{'request-checklist-item-checkbox-active': checkList.tier_level, 'request-checklist-item-checkbox': !checkList.tier_level}">
            <span class="mod-icon-check" style="width: 10px; height: 10px" />
          </span>
          <label><a
            :href="`/companies/edit/${$route.params.id}?tab=companies-tier`"
          >Tier level selected</a></label>
        </div>
        <div class="request-checklist-item rounded-bottom">
          <span :class="{'request-checklist-item-checkbox-active': checkList.company_billing, 'request-checklist-item-checkbox': !checkList.company_billing}">
            <span class="mod-icon-check" style="width: 10px; height: 10px" />
          </span>
          <label><a
            :href="`/companies/edit/${$route.params.id}?tab=billing-info`"
          >Billing details entered</a></label>
        </div>
      </div>
    </div>

    <button class="btn btn-primary btn-request mt-5" :disabled="isDisabledRequestButton" @click="showVisibilityRequestModal = true">
      Request to be visible on Mod24 website
    </button>


    <transition name="slide-fade">
      <div class="l-modal-mask" v-if="showVisibilityRequestModal" @click.self="showVisibilityRequestModal = false">
        <div class="sidebar-right flex flex-column justify-content-between pb-0">
          <div class="my-auto overflow-y-auto">
            <div @click="showVisibilityRequestModal = !showVisibilityRequestModal" class="pointer">
              <h1 class="modal-h1 l-m-b-20"><span class="btn btn-back" />Mod 24 visibility request</h1>
            </div>
            <div>
              <p class="px-1">
                We are glad you considered to use our online services!
              </p>
              <p class="mt-3 px-1">
                Before we proceed with making your company available to the potential customers, we will need to verify information you provided. It might take up to 48h th check and confirm your request.
              </p>
            </div>
            <div class="visibility-request-row mt-3">
              <div class="d-flex w-100 align-items-center justify-content-between">
                <div class="w-50 mr-3">
                  <h5 class="visibility-request-section-title">Your company details</h5>
                  <div class="d-flex align-items-center justify-content-between mt-3">
                    <span class="text-dark">Name</span>
                    <b class="text-blue">{{ company.name }}</b>
                  </div>
                  <div class="d-flex align-items-center justify-content-between mt-3">
                    <span class="text-dark">Licence number</span>
                    <b class="text-blue">{{ company.licence ? company.licence.licence_number : '/' }}</b>
                  </div>
                </div>
                <img width="100" :src="company.logo ? company.logo.url : ''" alt="Company logo" />
              </div>
            </div>
            <div class="visibility-request-row mt-4">
              <h5 class="visibility-request-section-title">Tier level</h5>
              <div class="d-flex align-items-center">
                <div class="tier-level mt-1">
                  <h5 class="mb-0">{{ company.mod_tier.name }}</h5>
                  <p class="tier-percentage">{{ company.mod_tier.percentage }}% commission</p>
                </div>
                <ul class="tier-options mb-0">
                  <li v-for="tier_option in company.mod_tier.mod_tier_options" :key="tier_option.id">{{ tier_option.name }}</li>
                </ul>
              </div>
            </div>
            <div class="visibility-request-row mt-3">
              <h5 class="visibility-request-section-title">Billing details</h5>
              <p class="mt-3 text-sm">
                This is account you've selected for your company on bill.com. We will send the invitation to our Mod24 network once we checked your visibility request. Please click the checkbox to confirm you've read and agree to our teams of service.
              </p>

              <div v-if="company.company_billing && parseInt(company.company_billing.pay_by, 10) !== 0" class="add-billing-options">
                <div class="billing-option billing-option-selected">
                  <div class="billing-details d-flex align-items-center">
                    <span class="mod-icon-bill mr-1" />
                    <div class="billing-option-info">
                      <span class="billing-option-title">Bill.com</span>
                      <p class="billing-option-description">Find your account by using network search or create account</p>
                    </div>
                  </div>
                  <span class="mod-icon-billing-option-checked" />
                </div>
              </div>
              <div v-if="company.company_billing && parseInt(company.company_billing.pay_by, 10) === 0" class="add-billing-options mt-3">
                <div class="billing-option billing-option-selected">
                  <div class="billing-details d-flex align-items-center">
                    <span class="mod-icon-billing-check mr-1" />
                    <div class="billing-option-info">
                      <span class="billing-option-title">Check payout</span>
                      <p class="billing-option-description">Select to receive payments as checks</p>
                    </div>
                  </div>
                  <span class="mod-icon-billing-option-checked" />
                </div>
              </div>
            </div>

            <div class="visibility-request-row mt-3">
              <h5 class="visibility-request-section-title">Service agreement</h5>
              <p class="mt-3 text-sm mb-3">
                The agreement is effective from the date when your visibility is approved by Mod24. Copy of this document will be sent to your email.
              </p>
              <div class="visibility-contract">
                <p>
                  <b>THIS AGREEMENT</b> is made on <span>{{ todayDate }}</span>, is entered into by and between, the Company and the Service Provider (Company and Service Provider collectively referred to herein as the “Parties” or individually as the “Party”). This Agreement shall become effective (the “Effective Date”) upon the date the visibility request of The Service Provider is approved by The Company.
                </p>

                <p class="mt-3 mb-2"><b>WHEREAS:</b></p>
                <ul>
                  <li>The Service Provider is a skilled and professional provider of logistic support services.</li>
                  <li>The Company operates a web-based business with the purpose of connecting logistic support service providers to potential customers.</li>
                  <li>The Company wishes to make the web-based business platform available to the Service Provider.</li>
                  <li>The Service Provider has indicated and warranted that it has the necessary skills, expertise, personnel and equipment to carry out and complete the Works and to the standards that are stated in this Agreement.</li>
                  <li>The Company now wishes to enter into this Agreement with the Service Provider under which the Service Provider will carry out and complete the Works.</li>
                </ul>

                <p>
                  <b>THEREFORE</b>, in consideration of mutual promises, representations, covenants and other good and valuable consideration, the receipt and adequacy of which is hereby acknowledged, the Parties agree to the following terms and conditions and to be bound thereby:
                </p>

                <p class="mt-3 mb-2"><span class="text-blue">DEFINITIONS AND INTERPRETATIONS</span></p>

                <p class="mb-2"><b>In this Agreement</b></p>

                <ul>
                  <li>“Agreement” means this Agreement, its Schedules, Exhibits and other documents (save as may otherwise be varied by this Agreement);</li>
                  <li>“Works” means providing logistic support services by transporting items from one location to another;</li>
                  <li>
                    In this Agreement, unless the context otherwise requires, any reference to:
                    <ul>
                      <li>the singular includes the plural and vice versa;</li>
                      <li>a person includes reference to a body corporate or other legal entity;</li>
                      <li>any written law includes that law as amended or re-enacted from time to time;</li>
                      <li>any agreement or other document includes that agreement or other document as varied or replaced by the Parties in writing from time to time;</li>
                      <li>a clause is to the relevant clause of this Agreement;</li>
                      <li>any Party includes that Party’s successors and assigns.</li>
                    </ul>
                  </li>
                  <li>Clause headings are inserted for convenience only and shall be ignored in construing this Agreement.</li>
                  <li>The identification of certain breaches in this Agreement as being “Material” does not indicate that only those breaches are material.</li>
                  <li>Where the Agreement provides for the giving or issue of any notice, consent, approval certificate or determination, it shall be in writing and the words notify, certify or determine shall be construed accordingly.  Routine communications relating to the performance of this Agreement may be conducted by electronic mail.</li>
                </ul>

                <p class="mt-3 mb-2"><span class="text-blue">APPLICATION OF TERMS AND THE SERVICE PROVIDER UNDERSTANDING</span></p>
                <ul>
                  <li>Save as may otherwise be provided in this Agreement the terms and conditions set out in this Agreement are the only terms on which the Company is prepared to deal with the Service Provider and they shall govern the relationship between the Parties to the entire exclusion of all other agreements entered into with the Company in relation to the provision of the Works.</li>
                  <li>If any ambiguity is found in the various documents forming this Agreement, the Company shall issue any necessary clarification or instruction.</li>
                  <li>All documents annexed to this agreement shall be subject to the terms under this agreement, provided that the Parties append their signatures on the documents.</li>
                </ul>

                <p class="mt-3 mb-2"><span class="text-blue">COMMENCEMENT AND DURATION</span></p>
                <p>This Agreement shall come into force upon execution and shall remain in force until either party terminates this Agreement.</p>

                <p class="mt-3 mb-2"><span class="text-blue">THE OBLIGATIONS OF THE PARTIES</span></p>
                <ul>
                  <li>The Service Provider will comply with the regulations and policies of the Company as made available in writing or through the Service Provider’s websites and/or software.</li>
                  <li>The Service Provider will supply the Company with all necessary information and documents as may be requested for by the Company for purposes of conducting a due diligence exercise to verify the background and status of the Service Provider.</li>
                  <li>The Service Provider will provide the Company with all necessary consents to enable the Company to conduct a background check on the Service Provider.</li>
                  <li>The Service Provider shall be solely responsible to ensure its assets and personnel are branded with the image and description of the Service Provider.</li>
                  <li>The Company will provide the Service Provider with its own page, containing images and information provided by the Service Provider. The Service Provider’s page will display ratings and reviews regarding the services rendered by the Service Provider. The Service Provider acknowledges and agrees that the Company has no control, directly or indirectly, over the content of the ratings and reviews that may be published on the Service Provider’s page.</li>
                </ul>

                <p class="mt-3 mb-2"><span class="text-blue">PAYMENT AND COMMISSION</span></p>
                <ul>
                  <li>The Company shall be entitled to a commission of <span>{{ company.mod_tier.percentage }}%</span>, which shall be deducted from each order received and processed via the Company’s platform.</li>
                  <li>All amounts due to the Service Provider shall be paid through either check, or by online payment service via <a href="https://www.bill.com">https://www.bill.com</a> on a monthly basis. In the event of any chargebacks by a customer, the Service Provider will be responsible for accrued chargebacks and the Company may, at its sole discretion, deduct chargebacks from the amount due to the Service Provider.</li>
                  <li>The Service Provider shall bear sole responsibility of declaring its earning to the relevant state/federal tax authority. The Service Provider agrees to discharge the Company of any damage, loss or injury arising from the Service Provider’s failure to comply with state/federal tax regulations.</li>
                </ul>

                <p class="mt-3 mb-2"><span class="text-blue">LIABILITY OF THE PARTIES</span></p>
                <ul>
                  <li>The Service Provider acknowledges and agrees that the Company is in the business of connecting the Service Provider to potential customers and shall not be held liable for any injury, damage or loss occasioned by the Service Provider and/or any potential customer, or any other third-party with whom the Service Provider has entered into a contract with. </li>
                  <li>
                    The Service Provider acknowledges and agrees that the Company shall not be held liable for any damage where:
                    <ul>
                      <li>The damage has been occasioned by the Service Provider, their representatives, employees and/or agents; and</li>
                      <li>The damage has been caused by an event beyond the control of the Service Provider.</li>
                    </ul>
                  </li>
                  <li>The Service Provider acknowledges and agrees that the Company shall not be held liable for acts and omissions of the Service Provider, where the same is the subject of a criminal offense;</li>
                  <li>The service Provider acknowledges and agrees that the Company shall not be held liable for the Service provider’s misuse, abuse or illegal action involving this Agreement, or any other agreement entered into between the Company and the Service Provider.</li>
                  <li>The Company shall be responsible for the performance and maintenance of its Platform and any disputes arising from the operation of such Platform shall be resolved by the Company.</li>
                </ul>

                <p class="mt-3 mb-2"><span class="text-blue">WARRANTIES</span></p>
                <ul>
                  <li>The Service Provider warrants that it is skilled and competent to perform the works.</li>
                  <li>
                    The Service Provider warrants that it will perform the Works with due care and diligence and to such high standard of quality consistent with applicable standards and as it is reasonable the Company to expect in all the circumstances;
                    <ul>
                      <li>in accordance with the provisions of this Agreement and such other instructions as may be given by the Company; and </li>
                      <li>in accordance with all applicable laws and consents.</li>
                    </ul>
                  </li>
                </ul>

                <p class="mt-3 mb-2"><span class="text-blue">INDEPENDENT SERVICE PROVIDER</span></p>
                <p>
                  The relationship of the parties hereto is that of independent contractors. The parties hereto are not deemed to be agents, partners or joint venturers of the others for any purpose as a result of this Agreement or the transactions contemplated thereby.
                </p>

                <p class="mt-3 mb-2"><span class="text-blue">NON-COMPETE</span></p>
                <p>
                  During the period of this Agreement and for a period of two (2) years following termination of this Agreement for any reason (the “Restricted Period”), Service Provider will not directly or indirectly, on its own behalf, or as a partner, officer, director, trustee, member, employee, or otherwise, within the United States or in any foreign market in which Service Provider was engaged in activities on behalf of the Company or any of its subsidiaries, own, engage in or participate in, in any way, any business that is similar to or competitive with any actual or planned business activity engaged in or planned by the Company or any of its subsidiaries at the time the employment under this Agreement was terminated.
                </p>
                <p>
                  During the period of this Agreement and for a period of two (2) years following termination of this Agreement for any reason (the “Restricted Period”), Service Provider will not directly or indirectly, on its own behalf, or as a partner, officer, director, trustee, member, employee, or otherwise attempt in any manner to solicit from any of the Company's clients, except on the Company's behalf, business of the type performed by the Company or to persuade any person, firm or corporation which is a client to cease doing business or to reduce the amount of business which any such client has customarily done or contemplates doing with the Company.
                </p>

                <p class="mt-3 mb-2"><span class="text-blue">INTELLECTUAL PROPERTY</span></p>
                <p>
                  The Service Provider agrees that any ideas, concepts, discoveries, techniques, patents, copyrights or trademarks relating to the business or operations of the Company and its Related Entities which are developed or discovered by the Service Provider, solely or jointly with others, during the subsistence of this Agreement, shall be deemed to have been made within the scope of this Agreement and therefore constitute works for hire and shall automatically upon their creation become the exclusive property of the Company.
                </p>

                <p class="mt-3 mb-2"><span class="text-blue">DISPUTE RESOLUTION</span></p>
                <p>
                  The Parties agree to attempt initially to solve all claims, disputes or controversies arising under, out of or in connection with this Agreement by conducting good faith negotiations. If the Parties are unable to settle the matter between themselves, the matter shall thereafter be resolved by alternative dispute resolution, starting with mediation and including, if necessary, a final and binding arbitration. Whenever a Party shall decide to institute arbitration proceedings, it shall give written notice to that effect to the other Party. The Party giving such notice shall refrain from instituting the arbitration proceedings for a period of sixty (60) days following such notice. During such period, the Parties shall make good faith efforts to amicably resolve the dispute without arbitration.
                </p>


                <p class="mt-3 mb-2"><span class="text-blue">TERMINATION</span></p>
                <ul>
                  <li>Either Party shall be entitled to terminate this Agreement immediately upon occurrence if there is suspicion of illegal activity by the other Party involving the Works.</li>
                  <li>Either Party may Terminate this agreement upon giving the other party no less than 30 days’ notice in writing. </li>
                  <li>
                    The Company may terminate this agreement immediately upon either of the following:
                    <ul>
                      <li>The Service Provider fails to deliver the Works within a period equivalent to double the agreed delivery time; or</li>
                      <li>The Service Provider breaches the terms of the Non-Disclosure Agreement.</li>
                    </ul>
                  </li>
                  <li>
                    The termination of this Agreement shall not discharge the liabilities accumulated by either Party.
                  </li>
                  <li>
                    Any Clauses intended by the Parties or this Agreement to survive the termination of this Agreement shall survive the termination of this Agreement by whatever cause.
                  </li>
                </ul>

                <p class="mt-3 mb-2"><span class="text-blue">VARIATION OF THE AGREEMENT</span></p>
                <p>
                  Either party may request variations to the Agreement. The Parties shall enter into discussions to agree on any required changes, revised pricing and time for performance.  Such variations will only be effective if agreed in writing by the Parties and recorded.
                </p>

                <p class="mt-3 mb-2"><span class="text-blue">FORCE MAJEURE</span></p>
                <ul>
                  <li>
                    For the purposes of this Agreement, <b>“Force Majeure”</b> means an event which could not reasonably have been avoided by a diligent party in the circumstances, which is beyond the reasonable control of a party and which makes a party’s performance of its responsibilities hereunder impossible or so impractical as reasonably to be considered impossible in the circumstances and includes, but is not limited to, war, riots, civil disorder, earthquake, storm, flood or adverse weather conditions, strikes, lockouts or other industrial action, terrorist acts, confiscation or any other action by government agencies.
                  </li>
                  <li>
                    Force Majeure shall not include any event which is caused by the negligence or intentional action of a Party or such Party’s subcontractor or agents or employees, or by a failure to observe good professional practice.
                  </li>
                  <li>
                    Force Majeure shall not include insufficiency of funds or failure to make any payment required hereunder.
                  </li>
                  <li>
                    The failure of a Party to fulfil any of its obligations hereunder shall not be considered to be a breach of, or default under, this Agreement insofar as such inability arises from an event of Force Majeure, provided that the Party affected by such an event has taken all reasonable precautions, due care and reasonable alternative measures, all with the objective of carrying out the terms of this Agreement.
                  </li>
                  <li>
                    A Party affected by an event of Force Majeure shall take all reasonable measures to remove such Party’s inability to fulfil its obligations hereunder with a minimum of delay.  The Parties shall take all reasonable measures to minimize the consequence of any event of Force Majeure.
                  </li>
                  <li>
                    A Party affected by an event of Force Majeure shall notify in writing the other Party of such event as soon as possible, and in any event not later than five (5) days following the occurrence of such event, providing evidence of the nature and cause of such event, and shall similarly give notice of the restoration of normal conditions as soon as possible.
                  </li>
                  <li>
                    Not later than fourteen (14) days after the Service Provider, as a result of an event of Force Majeure, has become unable to discharge a material portion of its obligations, the Parties shall consult with each other with a view to agreeing on appropriate measures to be taken in the circumstances.
                  </li>
                </ul>

                <p class="mt-3 mb-2"><span class="text-blue">CONFIDENTIALITY</span></p>
                <p>
                  Service Provider shall not at any time disclose, directly or indirectly to any other person whatsoever (including to the public or any section of the public) any information concerning this Agreement or any other information of any nature whatsoever concerning the Company, whether such information or matter is stated to be confidential or not, without the express written permission of the Company.  This covenant is given by the Service Provider on its own behalf.
                </p>

                <p class="mt-3 mb-2"><span class="text-blue">NO WAIVER</span></p>
                <p>
                  Except where this Agreement provides otherwise, the rights and remedies contained in it are cumulative and not exclusive to rights or remedies provided by law.  The failure by either Party to enforce at any time or for any period any one or more of the terms or conditions of this Agreement shall not be a waiver of them or of the right at any time subsequently to enforce all terms and conditions of this Agreement.
                </p>

                <p class="mt-3 mb-2"><span class="text-blue">SEVERABILITY</span></p>
                <p>
                  If any provision of this Agreement is declared by any judicial or other competent to be void, voidable, illegal or otherwise unenforceable, the Parties shall amend that provision in such reasonable manner as achieves the intention of the Parties without illegality or at the discretion of the Service Provider, it may be severed from this Agreement and the remaining provisions of this Agreement shall remain in full force and effect.
                </p>

                <p class="mt-3 mb-2"><span class="text-blue">NOTICES</span></p>
                <p>
                  The Parties select as their respective addresses, the addresses (including email) set out below for all purposes arising out of or in connection with this Agreement at which addresses only all processes and notices arising out of or in connection with this Agreement may validly be served upon or delivered by the Parties.
                </p>

                <div class="pl-3 my-3">
                  <p><b>THE COMPANY</b></p>
                  <p class="mt-2">Name: MOD24 LLC</p>
                  <p class="mt-2">Email: info@mod24.com</p>
                  <p class="mt-2">Address: 18580 Ventura blvd B, Tarzana, CA 91356</p>

                  <p class="mt-3 d-inner-block"><b>THE SERVICE PROVIDER</b></p>
                  <p class="mt-2">Name: {{ company.name }}</p>
                  <p class="mt-2">Email: {{ company.email }}</p>
                  <p class="mt-2">Address: {{ company.address }}, {{ company.city }}, {{ company.state.name }}, {{ company.zip }}</p>
                </div>

                <p class="mt-3 mb-2"><span class="text-blue">CHANGE OF ADDRESS</span></p>
                <p>
                  Either Party may provide changes in the above addresses by notice in writing given to the other Party as aforesaid.
                </p>

                <p class="mt-3 mb-2"><span class="text-blue">COSTS</span></p>
                <p>
                  Each party shall bear its own costs incurred in the negotiation, preparation and execution of this Agreement.
                </p>

                <p class="mt-3 mb-2"><span class="text-blue">GOVERNING LAW</span></p>
                <p>
                  The construction, validity and performance of this Agreement shall be governed in all respects by the Laws of California.
                </p>
              </div>
            </div>

            <p class="mt-5">
              <b>IN WITNESS WHEREOF</b>, each of the Parties has executed this Service Agreement, both Parties by its duly authorized officer, as of the day and year set forth below.
            </p>

            <div class="d-flex align-items-start visibility-signature mt-3">
              <div class="w-50 mr-3">
                <p>Signed by the duly authorized representative of <b>THE COMPANY</b></p>
                <div class="d-flex mt-3">
                  <div class="w-50">
                    Signature:
                  </div>
                  <div class="w-50 border-bottom-dashed">
                    S Karimov
                  </div>
                </div>
                <div class="d-flex">
                  <div class="w-50">
                    Name:
                  </div>
                  <div class="w-50 border-bottom-dashed">
                    Suhrab Karimov
                  </div>
                </div>
                <div class="d-flex">
                  <div class="w-50">
                    Designation:
                  </div>
                  <div class="w-50 border-bottom-dashed">
                    Director
                  </div>
                </div>
                <div class="d-flex">
                  <div class="w-50">
                    Date:
                  </div>
                  <div class="w-50 border-bottom-dashed">
                    <span>{{ todayDate }}</span>
                  </div>
                </div>
              </div>
              <div class="w-50 ml-3">
                <p>Signed by <b>SERVICE PROVIDER</b></p>

                <div class="d-flex mt-3">
                  <div class="w-50">
                    Signature:
                  </div>
                  <div class="w-50 border-bottom-dashed" />
                </div>
                <div class="d-flex">
                  <div class="w-50">
                    Name:
                  </div>
                  <div class="w-50 border-bottom-dashed" />
                </div>
                <div class="d-flex">
                  <div class="w-50">
                    Designation:
                  </div>
                  <div class="w-50 border-bottom-dashed" />
                </div>
                <div class="d-flex">
                  <div class="w-50">
                    Date:
                  </div>
                  <div class="w-50 border-bottom-dashed">
                    <span>{{ todayDate }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="visibility-request-row mt-3">
              <h5 class="visibility-request-section-title">Service provider authorized officer</h5>
              <p class="mt-3 text-sm">
                Please provide the following details and sign the contract
              </p>

              <div class="w-50 select-wrap mt-3">
                <label>Full name</label>
                <input type="text" class="form-control" v-model="visibilityRequestServiceProvider.full_name" />
              </div>
              <error-field :errors="errors" field="full_name" />

              <div class="w-50 select-wrap mt-3">
                <label>Designation</label>
                <input type="text" class="form-control" v-model="visibilityRequestServiceProvider.designation" />
              </div>
              <error-field :errors="errors" field="designation" />

              <div class="signature-tabs">
                <div @click="signatureType = 1" :class="{'signature-tab-selected': signatureType === 1}" class="signature-tab">
                  Draw signature
                </div>
                <div @click="signatureType = 2" :class="{'signature-tab-selected': signatureType === 2}" class="signature-tab">
                  Upload signature
                </div>
              </div>

              <template v-if="signatureType === 1">
                <p class="text-center my-4 text-signature">Use your mouse (or a finger on touch screen devices) to sign this document.</p>

                <div class="card signing-pad-card">
                  <button class="btn clear-button" @click="clear">
                    <i class="fa fa-eraser" aria-hidden="true" />
                    <span class="eraser-text">
                      CLEAR
                    </span>
                  </button>
                  <div class="d-flex flex-column justify-content-start">
                    <VueSignaturePad
                      width="100%"
                      height="250px"
                      ref="signaturePad"
                      :options="{ onEnd }"
                    />
                  </div>
                </div>
              </template>
              <template v-if="signatureType === 2">
                <p class="text-center my-4 text-signature">Select an image file for your signature.</p>

                <div class="card signing-pad-card">
                  <div class="d-flex justify-content-center w-100 p-3">
                    <input ref="uploadSignature" class="d-none" type="file" @change="selectFileForUploadSignature" />

                    <div class="upload-file" @click="$refs.uploadSignature.click()">
                      <span class="mod-icon-upload" />
                      {{ fileName ? fileName : 'Browse files' }}
                    </div>
                  </div>
                </div>
              </template>
              <error-field :errors="errors" field="signature" />


              <div class="terms mb-3">
                <div class="mt-3 d-flex align-items-center">
                  <input id="readAndSignedServiceAgreement" class="mr-3" type="checkbox" v-model="visibilityRequestServiceProvider.readAndSignedServiceAgreement" />
                  <label for="readAndSignedServiceAgreement" class="mb-0">I have read and signed the Service Agreement</label>
                </div>
                <div class="mt-3 d-flex align-items-center">
                  <input id="readAndAgreeTermsOfService" class="mr-3" type="checkbox" v-model="visibilityRequestServiceProvider.readAndAgreeTermsOfService" />
                  <label for="readAndAgreeTermsOfService" class="mb-0">I have read and agree to the <a target="_blank" :href="webSiteUrl + '/terms-and-conditions'">Terms of Service</a></label>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-3 top-shadow p-3 fix-width-bottom-bar">
            <div class="d-flex w-100 align-items-center justify-content-end">
              <button type="button" class="btn btn-not-active text-dark" @click="showVisibilityRequestModal = false">
                Cancel
              </button>

              <button @click="openVisibilitySendRequestModal" :disabled="!visibilityRequestServiceProvider.readAndSignedServiceAgreement || !visibilityRequestServiceProvider.readAndAgreeTermsOfService" class="btn btn-primary ml-3">
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>


    <modal v-if="showSendVisibilityRequest" @close="showSendVisibilityRequest = false" container="l-modal-container-sm">
      <template slot="header">&nbsp;</template>
      <div class="d-flex flex-column align-items-center px-3" slot="body">
        <img :src="require(`../../../assets/img/submit-request.svg`)" alt="Tier select" />
        <h4 class="text-center mt-4">You're about to submit a request to be visible on Mod24 website!</h4>
        <p class="text-center mt-3 px-3">
          Our team will verify your information within the next 48h. A reply will be sent to your company email address.
        </p>

        <p class="text-center mt-3 px-3">
          If you need to change any information, such as images, description, logo, licence number or payments details, it will need additional approval from our team.
        </p>

        <error-field :errors="errors" field="licence_number" />
        <error-field :errors="errors" field="note" />


        <div class="d-flex align-items-center justify-content-center w-100 mt-3">
          <button class="btn btn-secondary mr-1 w-50 btn-decline" @click="showSendVisibilityRequest = false">
            No, go back.
          </button>
          <button class="btn btn-primary ml-1 w-50 btn-accept" @click="requestVisibilityCompany">
            Yes, submit.
          </button>
        </div>
      </div>
      <template slot="footer">&nbsp;</template>
    </modal>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import axios from 'axios';
import toastr from 'toastr';
import VueSignaturePad from 'vue-signature-pad';
import moment from 'moment/moment';
import CompaniesStepsMenu from '../CompaniesStepsMenu';
import CompaniesVisibilityBar from '../CompaniesVisibilityBar';
import Errors from '../../../classes/Errors';
import Modal from '../../Modal';

export default {
  components: { CompaniesStepsMenu, CompaniesVisibilityBar, VueSignaturePad, Modal },
  props: {
    type: {
      type: String,
      default: 'edit',
    },
    company: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      assignmentTypes: [],
      buttonDisabled: false,
      loadingButton: false,
      showTooltipPrefix: false,
      checkList: {
        licence_number: false,
        request_note: false,
        logo: false,
        company_description: false,
        gallery_of_images: false,
        tier_level: false,
        company_billing: false,
      },
      showVisibilityRequestModal: false,
      visibilityRequestServiceProvider: {
        full_name: '',
        designation: '',
        signature: '',
        uploadSignature: '',
        readAndSignedServiceAgreement: false,
        readAndAgreeTermsOfService: false,
      },
      errors: new Errors(),
      showSendVisibilityRequest: false,
      webSiteUrl: process.env.WEBSITE_URL,
      todayDate: moment().format('L'),
      signatureTypes: [
        {
          name: 'Draw signature',
          id: 1,
        },
        {
          name: 'Upload signature',
          id: 2,
        },
      ],
      signatureType: 1,
      fileName: null,
    };
  },
  computed: {
    isDisabledRequestButton() {
      return (
        !this.checkList.licence_number ||
        !this.checkList.logo ||
        !this.checkList.company_description ||
        !this.checkList.tier_level ||
        !this.checkList.company_billing ||
        this.company.mod_status_id === 2 ||
        this.company.mod_status_id === 5
      );
    },
  },
  watch: {
    company() {
      this.setCheckList();
    },
  },
  mounted() {
    this.setCheckList();
  },
  methods: {
    ...mapMutations(['setCompanyStep']),
    openVisibilitySendRequestModal() {
      this.errors.clear();
      if (this.validationVisibilityRequestField()) {
        this.showSendVisibilityRequest = true;
      }
    },
    validationVisibilityRequestField() {
      if (!this.visibilityRequestServiceProvider.full_name) {
        this.errors.record({
          full_name: ['Full name is required field.'],
        });
        return false;
      }

      if (
        !this.visibilityRequestServiceProvider.signature &&
        !this.visibilityRequestServiceProvider.uploadSignature
      ) {
        this.errors.record({
          signature: ['Signature is required field.'],
        });
        return false;
      }

      if (!this.visibilityRequestServiceProvider.designation) {
        this.errors.record({
          designation: ['Designation is required field.'],
        });
        return false;
      }

      return true;
    },
    requestVisibilityCompany() {
      const requestData = {
        full_name: this.visibilityRequestServiceProvider.full_name,
        designation: this.visibilityRequestServiceProvider.designation,
        signature:
          this.signatureType === 1
            ? this.visibilityRequestServiceProvider.signature
            : this.visibilityRequestServiceProvider.uploadSignature,
        licence_number: this.company.licence ? this.company.licence.licence_number : '',
      };

      axios
        .post(`companies/${this.company.id}/request-review`, requestData)
        .then(() => {
          this.$emit('saved');
          toastr.success('Request sent for Mod24 review');
          this.showSendVisibilityRequest = false;
          this.showVisibilityRequestModal = false;
        })
        .catch(err => this.errors.record(err.response.data.error));
    },
    setCheckList() {
      this.checkList = {
        licence_number: this.company.licence && this.company.licence.is_valid,
        request_note: this.company.licence && this.company.licence.note,
        logo: this.company.logo,
        company_description: this.company.description && this.company.description.length,
        gallery_of_images: this.company.images && this.company.images.length,
        tier_level: this.company.mod_tier && this.company.mod_tier.id,
        company_billing: this.company.company_billing && this.company.company_billing.id,
      };
    },
    onEnd() {
      this.visibilityRequestServiceProvider.signature = this.$refs.signaturePad.saveSignature().data;
    },
    clear() {
      this.signature = '';
      this.$refs.signaturePad.clearSignature();
    },
    selectFileForUploadSignature(e) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = event => {
        this.$set(this.visibilityRequestServiceProvider, 'uploadSignature', event.target.result);
        this.fileName = file.name.split(/(\\|\/)/g).pop();
      };

      reader.readAsDataURL(file);
    },
  },
};
</script>

<style scoped lang="scss">
.mod-24-settings {
  border: 2px dashed #ba6ad7;
  background-color: #f6eef9;
  padding: 15px;
  border-radius: 5px;
  color: #ba6ad7;
  margin-top: 20px;
  max-width: 1100px;
}

.submit-text {
  width: 50%;
}

.request-checklist {
  margin-top: 15px;
  max-width: 300px;

  .request-checklist-title {
    text-transform: uppercase;
    font-weight: 500;
    font-size: 0.8rem;
  }

  .request-checklist-item {
    display: flex;
    align-items: center;
    padding: 10px;
    border: 1px solid #f3f3f3;

    label {
      margin: 0 0 0 10px;
      color: #2451d1;
      font-size: 0.9rem;
      font-weight: 400;
    }
  }

  .request-checklist-item-checkbox {
    background-color: #fdfdfd;
    width: 18px;
    height: 18px;
    border-radius: 5px;
    border: 1px solid #b5b3b3;
  }

  .request-checklist-item-checkbox-active {
    background-color: #0f47e2;
    width: 18px;
    height: 18px;
    border-radius: 5px;
    text-align: center;

    .mod-icon-check {
      margin-bottom: 2px;
    }
  }
}

.btn-request {
  min-width: 400px !important;
}

.visibility-request-section-title {
  color: #747474;
  text-transform: uppercase;
  font-size: 0.9rem;
  font-weight: 500;
}

.text-blue {
  color: #2961fe;
}

.text-sm {
  font-size: 0.8rem;
}

@media screen and (max-width: 600px) {
  .submit-text {
    width: 100%;
  }
}

.add-billing-select-text {
  text-transform: uppercase;
  font-weight: 500;
  font-size: 0.9rem;
}

.billing-option-selected {
  border: 1px solid #0f47e2 !important;
}

.billing-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 15px 5px 15px;
  border-radius: 5px;
  border: 1px solid #ffffff;
  cursor: pointer;

  .billing-option-info {
    display: flex;
    flex-direction: column;
    flex: 1;

    .billing-option-title {
      font-weight: 500;
    }

    .billing-option-description {
      font-size: 0.8rem;
    }
  }
}

.border-bottom-dashed {
  border-bottom: 1px dashed black;
}

.visibility-contract {
  color: black;
  height: 500px;
  overflow: auto;

  .text-blue {
    font-weight: bold;
  }
}

.overflow-y-auto {
  overflow-y: auto;
}

.top-shadow {
  -webkit-box-shadow: 0px -5px 30px -18px rgba(0, 0, 0, 0.75);
  -moz-box-shadow: 0px -5px 30px -18px rgba(0, 0, 0, 0.75);
  box-shadow: 0px -5px 30px -18px rgba(0, 0, 0, 0.75);
}

.fix-width-bottom-bar {
  margin: 0 -40px;
}

.visibility-signature {
  font-size: 0.9rem;
}

.signing-pad-card {
  padding: 10px 10px 10px 10px !important;
}

.clear-button {
  background: #f3f3f3;
  color: grey;
  border: none;
  max-width: 100px;
}

.eraser-text {
  font-size: 15px;
  font-weight: 500;
}

.text-signature {
  font-size: 14px;
}

.terms {
  label,
  a {
    font-size: 14px;
    font-weight: normal;
  }
}

.tier-options {
  font-size: 12px;
}

.tier-percentage {
  color: #7b9efe;
  font-size: 0.8rem;
}

.tier-level {
  h5 {
    line-height: 20px;
  }
}

.signature-tabs {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-top: 15px;

  .signature-tab {
    font-weight: bold;
    padding: 10px;
    color: #a7b0b9;
    cursor: pointer;
    line-height: 10px;
    font-size: 15px;
  }

  .signature-tab-selected {
    border-bottom: 2px solid #0f47e2;
    color: #000000 !important;
  }
}

.upload-file {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px 10px;
  background-color: #f3f3f3;
  text-transform: uppercase;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 5px;

  span {
    display: inline-block;
    margin-right: 5px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/companies/mod24settings/CompaniesGettingStarted.vue